import React, {
  createContext, FC, ReactNode, useContext, useEffect, useMemo, useState,
} from 'react'
import { NextRouter, useRouter } from 'next/router'
import { useTheme } from 'next-themes'
import isEqual from 'lodash/isEqual'

import {
  CustomerBranding, useBrandingImage, useCustomerBranding, usePublicBranding, useSsoBranding,
} from '@/api/branding'
import { loadGoogleFont } from '@/container/workflow-application-edit/font-picker'
import { BrandingSplash } from '@/layout/branding-splash'
import { fetchAuthCookie } from '@/utils/cookieUtil'
import { getCachedBranding, getUserProfile, storeCachedBranding } from '@/utils/localStorageUtil'
import { isModuleEnabled } from '@/utils/moduleUtil'
import {
  buildBrandingCss, hasBranding, isResellerHost, sanitizeFontFamily,
} from '@/utils/brandingThemeUtil'

/**
 * How long the app stays covered at most: until the branding answers, since showing Grid's
 * theme meanwhile is the flash the cover exists to stop, and then briefly for its font and
 * logo, which are only cosmetic. Either way a stalled request can't hide the app for good.
 */
export const MAX_BRANDING_WAIT_MS = { lookup: 10000, styling: 2000 }

type BrandingState = { branding: CustomerBranding | null, brandName: string | null, isPending: boolean }

const BrandingContext = createContext<BrandingState>({ branding: null, brandName: null, isPending: false })

/**
 * The branding the app is currently skinned with.
 * @returns the active branding, or null when the app shows Grid's own
 */
export const useBranding = () => useContext(BrandingContext).branding

/**
 * Whether the branding is still being looked up. Brand marks wait for it, so a reseller's
 * page does not flash Grid's logo before it is re-skinned.
 * @returns true until the lookup has answered
 */
export const useIsBrandingPending = () => useContext(BrandingContext).isPending

/**
 * The brand token Grid adds to an SSO callback, so a white-label customer on Grid's own
 * domain still sees their branding while signing in or on a sign-in error.
 * @param router - the Next router, or null outside one
 * @returns the token, or null off the SSO callback
 */
const getSsoBrandToken = (router: NextRouter | null): string | null => {
  // The router's pathname drops the locale prefix, so /es/auth/callback still matches.
  if (router?.pathname !== '/auth/callback' || router.query.method !== 'sso') return null
  return typeof router.query.brand === 'string' ? router.query.brand : null
}

/**
 * The name the server resolved for the active brand, which may be set without any theme.
 * @returns the brand's name, or null when the app shows Grid's own
 */
export const useBrandName = () => useContext(BrandingContext).brandName

const customerCacheKey = (customerId?: string) => `customer:${customerId}`
const hostCacheKey = (host: string) => `host:${host}`

type BrandingAnswer = { data?: CustomerBranding | null, isSuccess: boolean, isPlaceholderData: boolean }

/**
 * Keeps what a branding request answered, for the next page load to start from while it asks
 * again. Branding changed elsewhere therefore shows the old one for a moment on that load:
 * the price of not covering every load until the API answers.
 * @param cacheKey - the customer or host the branding was fetched for
 * @param answer - the branding query's result
 */
const useRememberedBranding = (cacheKey: string, { data, isSuccess, isPlaceholderData }: BrandingAnswer) => {
  useEffect(() => {
    if (isSuccess && !isPlaceholderData) storeCachedBranding(cacheKey, data ?? null)
  }, [cacheKey, data, isSuccess, isPlaceholderData])
}

/**
 * Picks the branding to skin the app with. A signed-in white-label customer sees what they
 * published; an SSO callback carrying a brand token sees that customer's; anyone else on a
 * reseller's host - their customers, and visitors to its login and sign-up pages - sees
 * that reseller's.
 * @returns the active branding, or null for Grid's own theme, and the brand's name
 */
const useActiveBranding = (): BrandingState => {
  const profile = getUserProfile()
  const isSignedIn = !!fetchAuthCookie()
  const hasWhiteLabel = isSignedIn && isModuleEnabled(profile?.customer?.modules?.whiteLabel)
  const host = typeof window === 'undefined' ? '' : window.location.hostname
  const isReseller = isResellerHost(host)
  const router = useRouter()
  const ssoBrandToken = getSsoBrandToken(router)
  // A static page's query is empty until hydration, so wait rather than flash Grid's artwork.
  const isAwaitingSsoQuery = router?.pathname === '/auth/callback' && !router.isReady

  const customerQuery = useCustomerBranding(profile?.customerId, {
    enabled: hasWhiteLabel, placeholderData: getCachedBranding(customerCacheKey(profile?.customerId)),
  })
  const { data: ssoBranding, isLoading: isSsoLoading } = useSsoBranding(ssoBrandToken)
  const hostQuery = usePublicBranding(host, { enabled: isReseller, placeholderData: getCachedBranding(hostCacheKey(host)) ?? undefined })
  // The API answers a host it can't match - a domain pointed at us before it is saved, or a
  // typo - with the internal customer's branding, and says nothing to tell the two apart.
  // An empty host always gets that fallback, so an identical answer means "no match".
  const fallbackQuery = usePublicBranding('', { enabled: isReseller, placeholderData: getCachedBranding(hostCacheKey('')) ?? undefined })
  useRememberedBranding(customerCacheKey(profile?.customerId), customerQuery)
  useRememberedBranding(hostCacheKey(host), hostQuery)
  useRememberedBranding(hostCacheKey(''), fallbackQuery)

  const { data: customerBranding, isLoading: isCustomerLoading } = customerQuery
  const { data: hostBranding, isLoading: isHostLoading } = hostQuery
  const { data: fallbackBranding, isLoading: isFallbackLoading } = fallbackQuery
  const isPending = (hasWhiteLabel && isCustomerLoading) || isAwaitingSsoQuery || (!!ssoBrandToken && isSsoLoading)
    || (isReseller && (isHostLoading || isFallbackLoading))

  const isHostMatched = isReseller && !!hostBranding && !isEqual(hostBranding, fallbackBranding)
  let brandName: string | null = null
  if (hasWhiteLabel && customerBranding) brandName = customerBranding.displayName || null
  else if (ssoBrandToken && ssoBranding) brandName = ssoBranding.displayName || null
  else if (isHostMatched) brandName = hostBranding?.displayName || null

  // A disabled query still holds what it fetched last, e.g. before a sign-out.
  if (hasWhiteLabel && hasBranding(customerBranding)) return { branding: customerBranding, brandName, isPending }
  if (ssoBrandToken && ssoBranding && hasBranding(ssoBranding)) return { branding: ssoBranding, brandName, isPending }
  if (isHostMatched && hasBranding(hostBranding)) return { branding: hostBranding, brandName, isPending }
  return { branding: null, brandName, isPending }
}

const getThemedLogo = (branding: CustomerBranding | null, resolvedTheme?: string) => (
  (resolvedTheme === 'dark' && branding?.logoDark) || branding?.logo
)

/**
 * Whether to cover the app while its branding loads. The cover goes up while the branding
 * itself is unknown - a page load, a sign-in - and stays until the font and logo it names
 * are ready, so the brand appears in one step. A font or logo changed later, e.g. by
 * publishing or switching theme, loads in place instead.
 * @param isPending - the branding is still being looked up
 * @param isStyling - its font or logo is still loading
 * @returns true while the app should be covered, for at most {@link MAX_BRANDING_WAIT_MS}
 */
const useBrandingCover = (isPending: boolean, isStyling: boolean) => {
  // A page load counts as an unknown branding, so one remembered from the last load still
  // waits for its font and logo.
  const [isCovering, setIsCovering] = useState(true)
  const [hasTimedOut, setHasTimedOut] = useState(false)
  let phase: keyof typeof MAX_BRANDING_WAIT_MS | null = null
  if (isPending) phase = 'lookup'
  else if (isCovering && isStyling) phase = 'styling'

  useEffect(() => {
    setIsCovering(!!phase)
    // Cleared only once nothing is awaited, so a cover that timed out never comes back.
    if (!phase) {
      setHasTimedOut(false)
      return undefined
    }
    const timer = setTimeout(() => setHasTimedOut(true), MAX_BRANDING_WAIT_MS[phase])
    return () => clearTimeout(timer)
  }, [phase])

  return !!phase && !hasTimedOut
}

type BrandingThemeProviderProps = { children: ReactNode }

/**
 * Applies the active branding to the whole app: colours, radius and font through the theme
 * tokens, and the favicon in the browser tab. Components read the logo with
 * {@link useBranding}. Until the branding is ready, a neutral splash covers the page, which
 * still renders and loads its data underneath.
 */
export const BrandingThemeProvider: FC<BrandingThemeProviderProps> = ({ children }) => {
  const { branding, brandName, isPending } = useActiveBranding()
  const state = useMemo(() => ({ branding, brandName, isPending }), [branding, brandName, isPending])
  const css = useMemo(() => (branding ? buildBrandingCss(branding) : ''), [branding])
  const font = sanitizeFontFamily(branding?.font)
  const [loadedFont, setLoadedFont] = useState('')
  const { resolvedTheme } = useTheme()
  const { data: favicon } = useBrandingImage(branding?.favicon)
  const { isLoading: isLogoLoading } = useBrandingImage(getThemedLogo(branding, resolvedTheme))
  const isCovered = useBrandingCover(isPending, (!!font && font !== loadedFont) || isLogoLoading)

  useEffect(() => {
    if (!font) return undefined
    let isCurrent = true
    loadGoogleFont(font).then(() => {
      if (isCurrent) setLoadedFont(font)
    })
    return () => { isCurrent = false }
  }, [font])

  // Repoints the icons `_document` declares rather than adding one: browsers pick between
  // several icon links by size, and would keep preferring Grid's sized ones.
  useEffect(() => {
    if (!branding?.favicon || !favicon) return undefined
    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'))
    const originals = links.map(link => link.getAttribute('href') ?? '')
    links.forEach(link => link.setAttribute('href', favicon))
    return () => links.forEach((link, index) => link.setAttribute('href', originals[index]))
  }, [branding?.favicon, favicon])

  return (
    <BrandingContext.Provider value={state}>
      {css && <style data-testid="branding-theme">{css}</style>}
      {children}
      {isCovered && <BrandingSplash />}
    </BrandingContext.Provider>
  )
}

type BrandingLogoImageProps = { fileId: string, className?: string, onClick?: () => void }

const BrandingLogoImage: FC<BrandingLogoImageProps> = ({ fileId, className, onClick }) => {
  const { data: src } = useBrandingImage(fileId)
  // Holds the space empty while the image loads rather than flashing Grid's logo.
  if (!src) return null
  return <img src={src} alt="" className={className} onClick={onClick} data-testid="branding-logo" />
}

type BrandingLogoProps = {
  className?: string
  onClick?: () => void
  /** Shown when no branded logo is set, so callers keep Grid's own mark. */
  fallback?: ReactNode
}

/**
 * The active branding's logo for the current light or dark theme.
 */
export const BrandingLogo: FC<BrandingLogoProps> = ({ className, onClick, fallback = null }) => {
  const branding = useBranding()
  const isPending = useIsBrandingPending()
  const { resolvedTheme } = useTheme()
  const logo = getThemedLogo(branding, resolvedTheme)

  if (isPending) return null
  return logo ? <BrandingLogoImage fileId={logo} className={className} onClick={onClick} /> : <>{fallback}</>
}
